import {
  PublicClientApplication,
  type Configuration,
  EventType,
  LogLevel,
} from "@azure/msal-browser";
import { config as runtimeConfig } from "../runtime-config";

const tenantId = runtimeConfig.entraTenantId;
const clientId = runtimeConfig.entraClientId;

const config: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    // Trailing slash matches what EntraID requires when registering a redirect
    // URI with no path segment, and what `azuread_application` validates.
    redirectUri: `${window.location.origin}/`,
    postLogoutRedirectUri: `${window.location.origin}/`,
  },
  cache: {
    cacheLocation: "sessionStorage", // safer than localStorage for tokens
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        // Surface MSAL Errors and Warnings to the browser console so we can
        // debug auth issues without a remote logger in dev.
        if (level === LogLevel.Error) {
          console.error("[MSAL]", message);
        } else if (level === LogLevel.Warning) {
          console.warn("[MSAL]", message);
        }
      },
    },
  },
};

export const msalInstance = new PublicClientApplication(config);

// Top-level await: ESM module suspends until MSAL is ready AND any pending
// redirect response is processed. Without an explicit handleRedirectPromise()
// the redirect-from-EntraID never advances the auth state and the user lands
// back on the SignIn screen.
await msalInstance.initialize();

try {
  const result = await msalInstance.handleRedirectPromise();
  if (result?.account) {
    msalInstance.setActiveAccount(result.account);
    console.info("[MSAL] login_success");
  } else {
    const accounts = msalInstance.getAllAccounts();
    // accounts[0] is AccountInfo|undefined under noUncheckedIndexedAccess;
    // setActiveAccount expects AccountInfo|null, hence the explicit fallback.
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0] ?? null);
    }
  }
} catch (e) {
  console.error("[MSAL] handleRedirectPromise failed", e);
}

msalInstance.addEventCallback((event) => {
  if (
    event.eventType === EventType.LOGIN_SUCCESS &&
    "payload" in event &&
    event.payload &&
    typeof event.payload === "object" &&
    "account" in event.payload &&
    event.payload.account
  ) {
    msalInstance.setActiveAccount(
      event.payload.account as Parameters<typeof msalInstance.setActiveAccount>[0],
    );
  }
});

export const apiScope = runtimeConfig.entraApiScope;

export const loginRequest = {
  scopes: [apiScope, "openid", "profile", "email"],
};
